import { FC, useState, useEffect, useRef, useMemo } from "react";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import PageInner from "@app/shared/PageInner";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESButton } from "@app/shared/qes-standards/Button";
import { toast } from "react-toastify";
import { IngestionRetryService } from "@app/operations/provider-hub-ingestion-retry/IngestionRetryService";
import { FailedIngestionProject } from "@app/operations/provider-hub-ingestion-retry/interfaces";
import { formatDateTime } from "@app/shared/formatDateTime";

interface FailedIngestionTableData {
  key: string;
  clientName: string;
  projectName: string;
  dateCreated: string;
}

export const IngestionRetry: FC = () => {
  const [data, setData] = useState<FailedIngestionProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const [skip, setSkip] = useState(0);

  const service = useRef(new IngestionRetryService()).current;

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await service.getFailedIngestions();
      setData(result);
    } catch (err) {
      setError("Failed to load provider hub ingestion retry data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* oxlint-disable sort-keys */
  const tableData: FailedIngestionTableData[] = useMemo(() => {
    return data.map(item => ({
      key: item.projectId.toString(),
      clientName: item.clientName,
      projectName: item.projectName,
      dateCreated: formatDateTime(item.dateCreated),
    }));
  }, [data]);

  const pagedTableData = useMemo(() => {
    return tableData.slice(skip, skip + pageSize);
  }, [tableData, skip, pageSize]);

  const columnConfig: ColumnConfig = useMemo<ColumnConfig>(() => {
    return {
      clientName: {
        displayText: "Client Name",
        testId: "client-name",
      },
      projectName: {
        displayText: "Project Name",
        testId: "project-name",
      },
      dateCreated: {
        displayText: "Date Created",
        testId: "date-created",
      },
    };
  }, []);
  /* oxlint-enable sort-keys */

  const paginationProps: WafflesPaginationProps = useMemo<WafflesPaginationProps>(() => {
    return {
      changePage: (pageNumber: number) => setSkip((pageNumber - 1) * pageSize),
      handlePageSizeChange: (newPageSize: number) => { setPageSize(newPageSize); setSkip(0); },
      pageSizeOptions: [10, 25, 50, 100],
      recordsPerPage: pageSize,
      showPageSizeSelector: true,
      skip,
      totalRecords: data.length,
    };
  }, [pageSize, skip, data.length]);

  const handleCheckboxChange = (keys: string[]) => {
    setSelectedKeys(keys);
  };

  const handleRetry = async () => {
    setLoading(true);
    const items = data
      .filter(item => selectedKeys.includes(item.projectId.toString()));

    try {
      const result = await service.retryIngestions(items);
      const queued = result.resetAndQueued.length;
      const skipped = result.skipped.length;

      if (queued > 0 && skipped === 0) {
        toast(`${queued} item(s) queued for ingestion.`, {
          containerId: "z-index-9999",
          type: "success",
        });
      } else if (queued > 0 && skipped > 0) {
        toast.warning(`${queued} item(s) queued for ingestion. ${skipped} item(s) were skipped.`, {
          containerId: "z-index-9999",
          type: "warning",
        });
      } else {
        toast.warning("Nothing queued, data may be stale refresh.", {
          containerId: "z-index-9999",
          type: "error",
        });
      }

      setSelectedKeys([]);
      setSkip(0);
      await loadData();
    } catch (err) {
      toast("An error occurred when retrying ingestion.", {
        containerId: "z-index-9999",
        type: "error",
      });
    }
  };

  return (
    <Page documentTitle="Provider Hub Ingestion Retry">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Provider Hub Ingestion Retry"]} />
      <PageInner>
        <PageHeader title="Provider Hub Ingestion Retry" disableHR={true} />
        <PageSection>
          <div className="d-flex justify-content-end mb-2 gap-2">
            <QESButton
              disabled={loading}
              onClick={loadData}
              qesTestId="provider-hub-ingestion-retry__refresh-button"
              qesType="secondary"
            >
              Refresh
            </QESButton>
            <QESButton
              disabled={selectedKeys.length === 0 || loading}
              onClick={handleRetry}
              qesTestId="provider-hub-ingestion-retry__retry-button"
              qesType="primary"
            >
              Retry
            </QESButton>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <Waffles<FailedIngestionTableData>
            columns={columnConfig}
            data={pagedTableData}
            enablePagination={true}
            fullWidth={true}
            handleCheckboxChange={handleCheckboxChange}
            loading={loading}
            paginationProps={paginationProps}
            selectedKeys={selectedKeys}
            showCheckboxes={true}
            testId="provider-hub-ingestion-retry-grid"
          />
        </PageSection>
      </PageInner>
    </Page>
  );
};
